// No-code configuration editor for REA Products & Product Requirements.
//
// Types are self-contained (not re-exported from the data-collection feature)
// so this editor compiles independently of that feature's evolving DTO — it
// reads from the backend's RequirementFieldDto. The Kotlin backend has
// `accept-case-insensitive-enums: true`, so the lowercase wire values used at
// read time are accepted verbatim on create/update — no casing dance.

export type RequirementFieldType = 'text' | 'number' | 'document' | 'date' | 'boolean' | 'select' | 'list';
export type RequirementEntityType = 'real_estate_asset' | 'building' | 'service';
export type RequirementFieldOptionSource = 'static' | 'buildings' | 'services';
export type DataCriticality = 'mandatory' | 'important' | 'optional' | 'irrelevant';

/**
 * Where a group renders on the REA page (GEN-591): top-level tab
 * (`data_collection`) or the Services → Information sub-tab
 * (`service_information`, SERVICE-entity fields only).
 */
export type RequirementGroupPlacement = 'data_collection' | 'service_information';

export interface RequirementFieldOption {
    value: string;
    label: string;
}

/**
 * A json-logic value (object = operator node, array = operands, leaf = literal).
 * Intentionally NON-recursive (children are `unknown`): a self-referential type
 * here makes `createAsyncThunk`'s deep payload inference blow up with TS2589.
 * The condition-mapper is the single place that walks this structure and casts
 * as needed.
 */
export type FieldCondition = boolean | number | string | null | { [key: string]: unknown } | unknown[];

/**
 * Mirrors the backend `RequirementFieldDto` (camelCase, lowercase enums).
 *
 * `dataCriticality` / `criticalityWhen` are per-product values injected from
 * the assignment row on product-scoped reads (GEN-642). The catalog read this
 * editor uses (`GET /v1/requirement-groups`) carries the neutral `'optional'`
 * default with no `criticalityWhen` — per-assignment criticality comes from
 * `GET /v1/product-requirement-field-assignments` instead.
 */
export interface RequirementField {
    id: string;
    code: string;
    productRequirementGroupId: string;
    name: string;
    label: string;
    placeholder: string | null;
    description: string | null;
    fieldType: RequirementFieldType;
    required: boolean;
    displayOrder: number;
    entityType: RequirementEntityType;
    targetFolder: string | null;
    visibleWhen: FieldCondition | null;
    requiredWhen: FieldCondition | null;
    parentFieldId: string | null;
    multiline: boolean;
    minRows: number | null;
    multiple: boolean;
    optionSource: RequirementFieldOptionSource | null;
    options: RequirementFieldOption[] | null;
    listSize: FieldCondition | null;
    dataCriticality: DataCriticality;
    criticalityWhen: FieldCondition | null;
    createdAt?: string;
    updatedAt?: string;
}

/** A field with its LIST sub-fields attached — derived client-side from the flat rows. */
export interface RequirementFieldWithChildren extends RequirementField {
    subFields: RequirementFieldWithChildren[];
}

/** Wire shape from `GET /v1/requirement-groups` (full catalog) — flat fields (parents + children). */
export interface ProductRequirementGroup {
    id: string;
    code: string;
    name: string;
    displayOrder: number;
    placement: RequirementGroupPlacement;
    requirementFields: RequirementField[];
}

/** Wire shape from `GET /v1/products`. */
export interface ProductSummary {
    id: string;
    name: string;
    sku: string;
}

/**
 * Wire shape from `GET /v1/product-requirement-field-assignments`: one
 * product↔field edge with its per-product criticality (GEN-642).
 */
export interface FieldAssignment {
    productId: string;
    fieldId: string;
    dataCriticality: DataCriticality;
    criticalityWhen: FieldCondition | null;
}

/** Body of `PUT /v1/products/{productId}/requirement-fields/{fieldId}` (assign / update criticality). */
export interface AssignmentRequest {
    dataCriticality: DataCriticality;
    criticalityWhen: FieldCondition | null;
}

/**
 * Create payload — mirrors the backend `CreateRequirementFieldRequestDto`.
 * Carries NO criticality: that is per-product and set at assign time (GEN-642).
 */
export interface CreateRequirementFieldRequest {
    code: string;
    name: string;
    label: string;
    placeholder?: string | null;
    description?: string | null;
    fieldType: RequirementFieldType;
    required: boolean;
    displayOrder: number;
    entityType: RequirementEntityType;
    targetFolder?: string | null;
    visibleWhen?: FieldCondition | null;
    requiredWhen?: FieldCondition | null;
    parentFieldId?: string | null;
    multiline: boolean;
    minRows?: number | null;
    multiple: boolean;
    optionSource?: RequirementFieldOptionSource | null;
    options?: RequirementFieldOption[] | null;
    listSize?: FieldCondition | null;
}

/**
 * Update payload — mirrors the backend `UpdateRequirementFieldRequestDto`:
 * the create shape MINUS `code` (immutable identifier — conditions and
 * collected values key on it) and `parentFieldId` (structure is fixed).
 */
export type UpdateRequirementFieldRequest = Omit<CreateRequirementFieldRequest, 'code' | 'parentFieldId'>;

export interface CreateGroupRequest {
    code: string;
    name: string;
    displayOrder: number;
    placement: RequirementGroupPlacement;
}

/**
 * `code` is immutable (conditions reference it) — label, order and placement
 * are editable, though the backend rejects a placement change while the group
 * still has fields (placement binds the fields' entity type, GEN-591).
 */
export interface UpdateGroupRequest {
    name: string;
    displayOrder: number;
    placement: RequirementGroupPlacement;
}

/**
 * Build the top-level fields with their LIST sub-fields attached. The backend
 * sends a flat list; sub-fields point to their parent via `parentFieldId`.
 */
export function buildFieldTree(fields: RequirementField[]): RequirementFieldWithChildren[] {
    const byId = new Map<string, RequirementFieldWithChildren>();
    for (const f of fields) byId.set(f.id, { ...f, subFields: [] });
    const roots: RequirementFieldWithChildren[] = [];
    for (const f of byId.values()) {
        if (f.parentFieldId && byId.has(f.parentFieldId)) byId.get(f.parentFieldId)!.subFields.push(f);
        else if (!f.parentFieldId) roots.push(f);
    }
    for (const f of byId.values()) f.subFields.sort((a, b) => a.displayOrder - b.displayOrder);
    return roots.sort((a, b) => a.displayOrder - b.displayOrder);
}
